import { useEffect, useMemo, useState } from 'react';
import { usePlatformConfig } from '../contexts/PlatformConfigContext';
import {
  shouldOfferStaffGoogleLink,
  type StaffGoogleOnboardingProfile,
} from '../core/rules/staffGoogleOnboarding';
import { usesFirebaseAuth } from './authSession';
import { listenUserDoc } from './listenUserDoc';
import { useFailOpenTimeout } from './useFailOpenTimeout';
import type { AppUser } from '../types';

function profileFromUserDoc(data: Record<string, unknown> | null): StaffGoogleOnboardingProfile | null {
  if (!data) return null;
  return {
    googleSignInRecommended: data.googleSignInRecommended === true,
    googleLinked: data.googleLinked === true,
    googleLinkPromptDismissed: data.googleLinkPromptDismissed === true,
  };
}

export function useStaffGoogleOnboarding(user: AppUser | null) {
  const { config: platformConfig } = usePlatformConfig();
  const firebaseAuth = usesFirebaseAuth();
  const [profile, setProfile] = useState<StaffGoogleOnboardingProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!firebaseAuth || !user?.id) {
      setProfile(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    return listenUserDoc(
      user.id,
      (data) => {
        setProfile(profileFromUserDoc(data));
        setLoading(false);
      },
      () => {
        setProfile(null);
        setLoading(false);
      },
    );
  }, [firebaseAuth, user?.id]);

  const timedOut = useFailOpenTimeout(Boolean(firebaseAuth && user?.id));
  const waiting = loading && !timedOut;

  const shouldShow = useMemo(() => shouldOfferStaffGoogleLink({
    role: String(user?.role ?? ''),
    firebaseAuth,
    platformConfig,
    profile,
  }), [user?.role, firebaseAuth, platformConfig, profile]);

  return { loading: waiting, shouldShow, profile };
}
